import React, { useState } from "react";
import { useRegisterUserMutation } from "../services/apiSlice";
import { useNavigate, Link } from "react-router-dom";
import { 
  User, Phone, Mail, UserCircle, 
  Calendar, MapPin, Briefcase, Shield,
  Lock, CheckCircle, ArrowRight, AlertCircle,
  Loader2, Building, FileText
} from 'lucide-react';

const Register = () => {
  const [formData, setFormData] = useState({
    userName: "",
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    nic: "",
    address: "",
    dob: "",
    occupation: "",
  });

  const [errors, setErrors] = useState({});
  const [registerUser, { isLoading }] = useRegisterUserMutation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Validation Rules
  const validateForm = () => {
    const newErrors = {};

    // Username validation: 4-20 characters, alphanumeric and underscore
    if (!formData.userName.trim()) {
      newErrors.userName = "Username is required";
    } else if (formData.userName.trim().length < 4) {
      newErrors.userName = "Username must be at least 4 characters long";
    } else if (formData.userName.trim().length > 20) {
      newErrors.userName = "Username must not exceed 20 characters";
    } else if (!/^[a-zA-Z0-9_]{4,20}$/.test(formData.userName)) {
      newErrors.userName = "Username can only contain letters, numbers, and underscores";
    }

    // Phone validation: exactly 10 digits
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }

    // Email validation: proper email format
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address (e.g., user@example.com)";
    }

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters long";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters long";
    }

    // NIC validation
    if (!formData.nic.trim()) {
      newErrors.nic = "NIC is required";
    } else if (formData.nic.trim().length < 9) {
      newErrors.nic = "NIC must be at least 9 characters long";
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    } else if (formData.address.trim().length < 5) {
      newErrors.address = "Address must be at least 5 characters long";
    }

    // Date of Birth validation
    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
    } else {
      const selectedDate = new Date(formData.dob);
      const today = new Date();
      const age = today.getFullYear() - selectedDate.getFullYear();
      if (age < 18) {
        newErrors.dob = "You must be at least 18 years old";
      }
      if (selectedDate > today) {
        newErrors.dob = "Date of birth cannot be in the future";
      }
    }

    // Occupation validation
    if (!formData.occupation.trim()) {
      newErrors.occupation = "Occupation is required";
    } else if (formData.occupation.trim().length < 2) {
      newErrors.occupation = "Occupation must be at least 2 characters long";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const res = await registerUser(formData).unwrap();
      alert(res?.Message || res?.Result || "Registration successful! Please login.");
      setFormData({
        userName: "",
        phone: "",
        email: "",
        firstName: "",
        lastName: "",
        nic: "",
        address: "",
        dob: "",
        occupation: "",
      });
      navigate("/login");
    } catch (err) {
      console.error(err);
      alert(err?.data?.Message || err?.data?.Result || "Registration failed. Please try again.");
    }
  };

  const steps = [
    { number: 1, label: "Account Details", icon: UserCircle },
    { number: 2, label: "Personal Info", icon: User },
    { number: 3, label: "Additional Info", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-lg">
            <User className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Create Your Account
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Join Smart Lend today and experience intelligent lending solutions
          </p>
        </div>

        {/* Progress Steps */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="flex items-center justify-between relative">
            {/* Progress Bar */}
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2 -z-10"></div>
            
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              
              return (
                <div key={step.number} className="flex flex-col items-center relative z-10">
                  <div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg' 
                        : isActive
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg'
                        : 'bg-white border-2 border-gray-300'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-white" />
                    ) : (
                      <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    isActive || isCompleted ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Side - Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Registration Form</h2>
                  <p className="text-gray-600">Fill in your details to get started</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Row 1: Username & Phone */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Username
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="userName"
                        name="userName"
                        placeholder="johndoe_123"
                        value={formData.userName}
                        onChange={handleChange}
                        className={`w-full pl-12 pr-4 py-3 bg-gray-50 border-2 ${
                          errors.userName ? 'border-red-300' : 'border-gray-200'
                        } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                        disabled={isLoading}
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <UserCircle className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.userName && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.userName}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">4-20 characters, letters, numbers, underscores</p>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="0712345678"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        className={`w-full pl-12 pr-4 py-3 bg-gray-50 border-2 ${
                          errors.phone ? 'border-red-300' : 'border-gray-200'
                        } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                        disabled={isLoading}
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <Phone className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.phone && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.phone}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Exactly 10 digits required</p>
                  </div>
                </div>

                {/* Email */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-12 pr-4 py-3 bg-gray-50 border-2 ${
                        errors.email ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <Mail className="w-5 h-5" />
                    </div>
                  </div>
                  {errors.email && (
                    <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </div>
                  )}
                </div>

                {/* First Name & Last Name */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.firstName ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.firstName && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.firstName}
                      </div>
                    )}
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.lastName ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.lastName && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.lastName}
                      </div>
                    )}
                  </div>
                </div>

                {/* NIC & DOB */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      NIC / ID Number
                    </label>
                    <input
                      type="text"
                      id="nic"
                      name="nic"
                      placeholder="987654321V"
                      value={formData.nic}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.nic ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.nic && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.nic}
                      </div>
                    )}
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      id="dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.dob ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.dob && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.dob}
                      </div>
                    )}
                  </div>
                </div>

                {/* Address & Occupation */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      placeholder="123 Main Street, City"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.address ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.address && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.address}
                      </div>
                    )}
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Occupation
                    </label>
                    <input
                      type="text"
                      id="occupation"
                      name="occupation"
                      placeholder="Software Engineer"
                      value={formData.occupation}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                        errors.occupation ? 'border-red-300' : 'border-gray-200'
                      } text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all`}
                      disabled={isLoading}
                    />
                    {errors.occupation && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {errors.occupation}
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Note */}
                <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium mb-1">Security Information</p>
                      <p className="text-sm text-blue-700">
                        Your personal information is securely encrypted and protected. We never share your data with third parties.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                      isLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating Account...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Create Account
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </button>
                </div>

                {/* Login Link */}
                <div className="text-center pt-6 border-t border-gray-200">
                  <p className="text-gray-600">
                    Already have an account?{' '}
                    <Link 
                      to="/login" 
                      className="text-blue-600 font-semibold hover:text-blue-700 transition-colors inline-flex items-center gap-1"
                    >
                      Login here
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Right Side - Benefits */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-b from-blue-50 to-white rounded-3xl border border-blue-100 p-8 shadow-xl h-fit sticky top-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Join Smart Lend?</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Quick Approvals</h4>
                      <p className="text-sm text-gray-600">Get loan approvals within 24 hours with our AI-powered system</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Bank-Level Security</h4>
                      <p className="text-sm text-gray-600">256-bit encryption ensures your data is always protected</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Competitive Rates</h4>
                      <p className="text-sm text-gray-600">Enjoy some of the most competitive interest rates in the market</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">24/7 Support</h4>
                      <p className="text-sm text-gray-600">Our customer support team is available round the clock</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requirements Card */}
              <div className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-3">Requirements</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    </div>
                    <span>Must be 18 years or older</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    </div>
                    <span>Valid NIC or ID document</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    </div>
                    <span>Active phone number</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    </div>
                    <span>Valid email address</span>
                  </li>
                </ul>
              </div>

              {/* Privacy Notice */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  By creating an account, you agree to our{' '}
                  <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
                  and{' '}
                  <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>Need help? Contact our support team at support@smartlend.com</p>
        </div>
      </div>
    </div>
  );
};

export default Register;